import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  GRID_VIEW_ACTIONS,
  GRID_VIEW_DATA_ACTIONS,
  GRID_VIEW_DIALOG_ACTIONS,
} from '../actions';
import { exhaustMap, of, switchMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GridViewDialogComponent } from '../dialogs/components';
import { catchError } from 'rxjs/operators';
import { GridView } from '../types';
import { GridViewService } from '../services';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class GridViewEffects {
  private readonly _actions$ = inject(Actions); 
  private readonly _gridService = inject(GridViewService);  
  private readonly _store = inject(Store);  
  private readonly _dialog = inject(MatDialog); 
  private readonly _snackBar = inject(MatSnackBar); 
  
  private _dialogRef!: MatDialogRef<GridViewDialogComponent>;
  /**
   * @Save
   * */
  saveGrid$ = createEffect(() =>
    this._actions$.pipe(
      ofType(GRID_VIEW_ACTIONS.onSaveGridView),
      switchMap((g) =>
        this._gridService.saveGridView(g.gridView).pipe(
          switchMap((gridView: GridView) => {
            return [
              GRID_VIEW_DIALOG_ACTIONS.closeGridViewDialog(),
              GRID_VIEW_ACTIONS.onSaveGridViewSuccess({
                gridView: gridView,
                grid_type: g.grid_type,
              }),
              GRID_VIEW_DATA_ACTIONS.setCustomViewSelection({
                selectedView: {
                  id: gridView.id,
                  label: gridView.name as string,
                },
              }),
            ];
          }),
          catchError((err: string) =>
            of(
              GRID_VIEW_ACTIONS.onSaveGridViewFailure({
                errorHandler: {
                  error: err,
                  isFormInValid: true,
                },
                grid_type: g.grid_type,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * @Update
   * */
  bulkUpdateGridViews$ = createEffect(() =>
    this._actions$.pipe(
      ofType(GRID_VIEW_ACTIONS.onBulkUpdateGridViews),
      switchMap(({ grid_type, grid_views }) =>
        this._gridService
          .bulkGridViewUpdate({
            grid_views: grid_views,
            grid_type: grid_type,
          })
          .pipe(
            tap(() => this._snackBarMessage('Changes saved')),
            switchMap((response) => {
              return [
                GRID_VIEW_DIALOG_ACTIONS.closeGridViewDialog(),
                GRID_VIEW_ACTIONS.onBulkUpdateGridViewSuccess({
                  grid_type: grid_type,
                  grid_views: response,
                }),
              ];
            }),
            catchError((err: string) =>
              of(
                GRID_VIEW_ACTIONS.onBulkUpdateGridViewFailure({
                  errorHandler: {
                    error: err,
                  },
                  grid_type: grid_type,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateGridView$ = createEffect(() =>
    this._actions$.pipe(
      ofType(GRID_VIEW_ACTIONS.onUpdateGridView),
      switchMap(({ grid_type, gridView }) =>
        this._gridService
          .updateGridView({
            grid_type,
            gridView,
          })
          .pipe(
            tap(() => this._snackBarMessage('View saved')),
            switchMap((gridView) => {
              // @ToDo
              return [
                GRID_VIEW_DIALOG_ACTIONS.closeGridViewDialog(),
                GRID_VIEW_ACTIONS.onUpdateGridViewSuccess({
                  gridView: gridView,
                  grid_type: grid_type,
                }),
              ];
            }),
          ),
      ),
    ),
  );

  /**
   * @Fetch
   * */
  getTotalGridViews$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(GRID_VIEW_ACTIONS.getGridViewsAction),
        exhaustMap((g) =>
          this._gridService.getTotalGridViews(g.gridType).pipe(
            tap((gridViews) =>
              this._store.dispatch(
                GRID_VIEW_ACTIONS.onGetGridViewsSuccess({
                  grid: gridViews,
                  grid_type: g.gridType,
                }),
              ),
            ),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  /**
   * @Dialog Actions
   * */
  openGridViewDialog = createEffect(
    () =>
      this._actions$.pipe(
        ofType(GRID_VIEW_DIALOG_ACTIONS.openGridViewDialog),
        tap(
          ({ data }) =>
            (this._dialogRef = this._dialog.open(GridViewDialogComponent, {
              autoFocus: data.config.autoFocus,
              data: data,
              width: data.config.width,
            })),
        ),
      ),
    {
      dispatch: false,
    },
  );

  closeGridViewDialog = createEffect(
    () =>
      this._actions$.pipe(
        ofType(GRID_VIEW_DIALOG_ACTIONS.closeGridViewDialog),
        tap(() => this._dialogRef.close()),
      ),
    {
      dispatch: false,
    },
  );

  private _snackBarMessage(message: string, action = ''): void {
    this._snackBar.open(message, action, {
      duration: 5000,
      horizontalPosition: 'start',
      verticalPosition: 'bottom',
    });
  }
}
